/**
 * venueTime — the single home for formatting a VENUE-LOCAL wall clock (BON-386).
 *
 * THE INPUT IS A NAIVE VENUE-LOCAL WALL CLOCK — an ISO datetime with NO trailing
 * `Z` (e.g. `2026-08-11T14:00:00`), the venue's wall-clock time as delivered by
 * the backend's `localStartDatetime` / `localEndDatetime` fields. It must be
 * rendered EXACTLY as typed — never re-anchored to the viewer's timezone. UTC is
 * the comparison key and is never displayed (design D10); `startsAt` / `endsAt`
 * (absolute, `Z`-tagged) are for sort/compare ONLY.
 *
 * Mirrors the booking app's `booking-format.ts`, expressed in the frontend's
 * date-fns idiom: `parseISO` on a no-`Z` string yields a Date whose LOCAL
 * components ARE the wall clock, and `format` reads those same local components
 * back — so the digits round-trip regardless of the runtime timezone, with no
 * zone shift. (The booking app formats-in-`UTC` instead because it renders
 * server-side under SSR; the staff app is client-only, so parse-local is both
 * correct and the codebase's existing idiom.)
 *
 * Do NOT pass the absolute `startsAt` / `endsAt` here. A trailing zone is
 * stripped defensively so a stray instant still renders its literal clock rather
 * than a shifted one, but display must source the venue-local fields.
 */
import { format, parseISO } from 'date-fns';

/** A trailing `Z` or `±HH:MM` / `±HHMM` offset — the mark of a zoned string. */
const ZONE_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Drop any trailing zone designator so the value parses as a naive wall clock. */
function stripZone(iso: string): string {
  return iso.replace(ZONE_SUFFIX, '');
}

/**
 * Parse a naive venue-local ISO into a Date whose LOCAL getters are the wall
 * clock (`getHours()` / `getDate()` return the venue's numbers, not UTC's).
 */
export function parseVenueLocal(iso: string): Date {
  return parseISO(stripZone(iso));
}

/** The wall-clock hour/minute of a naive venue-local ISO, or null when the input
 *  is missing/unparseable. For the bespoke clock labels that date-fns patterns
 *  can't express (single-letter am/pm, minutes only when non-zero). */
export function venueWallClock(
  iso: string | null | undefined,
): { hours: number; minutes: number } | null {
  if (!iso) return null;
  const d = parseVenueLocal(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { hours: d.getHours(), minutes: d.getMinutes() };
}

/** date-fns pattern for the default short date, e.g. "Thu, Oct 1". */
const DEFAULT_DATE_PATTERN = 'EEE, MMM d';
/** date-fns pattern for the default 12-hour time, e.g. "6:30 PM". */
const DEFAULT_TIME_PATTERN = 'h:mm a';

/**
 * Format a naive venue-local ISO as a date, WITHOUT any timezone conversion.
 * Null/empty/unparseable → `fallback` ("—" by default).
 *
 * @param iso     venue-local wall clock, no trailing `Z` (may be null)
 * @param pattern any date-fns format pattern (default "EEE, MMM d")
 */
export function formatVenueDate(
  iso: string | null | undefined,
  pattern: string = DEFAULT_DATE_PATTERN,
  fallback = '—',
): string {
  if (!iso) return fallback;
  const d = parseVenueLocal(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return format(d, pattern);
}

/**
 * Format a naive venue-local ISO as a time, WITHOUT any timezone conversion.
 * Null/empty/unparseable → `fallback` ("—" by default).
 *
 * @param iso     venue-local wall clock, no trailing `Z` (may be null)
 * @param pattern any date-fns format pattern (default "h:mm a")
 */
export function formatVenueTime(
  iso: string | null | undefined,
  pattern: string = DEFAULT_TIME_PATTERN,
  fallback = '—',
): string {
  if (!iso) return fallback;
  const d = parseVenueLocal(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return format(d, pattern);
}

/**
 * Format an absolute instant (a `Z`-tagged ISO) as the venue's own date and time,
 * e.g. "Oct 14, 3:05 PM". For moments that exist only as an instant, such as when
 * something was recorded; workshop times use the wall-clock formatters above.
 * Missing input, a missing zone or an unknown zone → `fallback`.
 */
export function formatInstantInVenueZone(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  fallback = '—',
): string {
  if (!iso || !timeZone) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    return fallback;
  }
}
